"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/* =============================================================================
 * TYPES & INTERFACES
 * ============================================================================= */

interface VendorAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  latitude?: number;
  longitude?: number;
}

interface VendorVerificationStatus {
  is_verified: boolean;
  verified_at: string | null;
  verified_by: number | null;
}

interface VendorDetail {
  business_type: string | null;
  price_range: string;
  cuisine_types: string[];
  features: string[];
  amenities: string[];
  tags: string[];
  logo_url: string | null;
  cover_url: string | null;
  average_rating: number;
  total_reviews: number;
}

export interface Vendor {
  id: number;
  vendor_code: string;
  name: string;
  slug: string;
  description: string | null;
  address: VendorAddress | null;
  contact_email: string;
  contact_phone: string;
  status: "active" | "pending" | "inactive" | "suspended" | "rejected";
  is_verified: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  detail: VendorDetail;
  average_rating?: number;
  owner_id: number;
  created_by: number | null;
  verification_status: VendorVerificationStatus;
  user_role: string;
}

export interface MenuItem {
  id: number;
  name: string;
  description: string;
  price: number;
  category_id: number;
  vendor_id: number;
  image_url: string | null;
  is_available: boolean;
  dietary_tags: string[];
  calories?: number;
  preparation_time: number;
  customization_options?: Array<{
    id: number;
    name: string;
    choices: Array<{
      id: number;
      name: string;
      price_adjustment: number;
    }>;
    required: boolean;
  }>;
}

export interface CartItem extends MenuItem {
  quantity: number;
  customizations?: Array<{
    option_id: number;
    choice_id: number;
  }>;
  special_instructions?: string;
  addedAt: number;
  totalPrice: number;
}

interface CartSummary {
  subtotal: number;
  tax: number;
  serviceFee: number;
  deliveryFee: number;
  total: number;
  itemCount: number;
  uniqueItems: number;
}

interface CurrentVendorCartSnapshot {
  vendor_id: number | null;
  vendor_name: string | null;
  item_count: number;
  unique_items: number;
  items: CartItem[];
}

interface VendorContextType {
  // Vendor Management
  selectedVendor: Vendor | null;
  setSelectedVendor: (vendor: Vendor | null) => void;
  clearSelectedVendor: () => void;

  // Cart Management
  cart: CartItem[];
  cartSummary: CartSummary;
  currentVendorId: number | null;
  currentVendorName: string | null;

  // Convenience snapshot used by CartProvider (fixes your crash)
  currentVendorCart: CurrentVendorCartSnapshot;

  // Cart Operations
  addToCart: (
    item: MenuItem,
    quantity: number,
    customizations?: any,
    instructions?: string
  ) => void;
  removeFromCart: (itemId: number, customizations?: any) => void;
  updateCartItem: (
    itemId: number,
    updates: {
      quantity?: number;
      customizations?: any;
      instructions?: string;
    }
  ) => void;
  clearCart: () => void;

  // Validation & Checks
  validateCartForVendor: (vendorId: number) => boolean;
  hasCartConflict: (vendorId: number) => boolean;
  isCartEmpty: boolean;

  // Persistence
  saveCart: () => void;
  loadCart: () => void;

  // State
  isCartLoading: boolean;
  lastCartUpdate: number;
  cartVersion: string;
}

/* =============================================================================
 * CONSTANTS & CONFIG
 * ============================================================================= */

const CART_STORAGE_KEY = "indemenu_cart_v2";
const CART_VERSION = "1.0.0";
const CART_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
const TAX_RATE = 0.08; // 8%
const SERVICE_FEE_RATE = 0.05; // 5%
const DELIVERY_FEE = 2.99; // Fixed delivery fee

/* =============================================================================
 * CONTEXT CREATION
 * ============================================================================= */

const VendorContext = createContext<VendorContextType | undefined>(undefined);

/* =============================================================================
 * PROVIDER COMPONENT
 * ============================================================================= */

interface VendorProviderProps {
  children: ReactNode;
  initialVendor?: Vendor | null;
}

function safeJsonParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function sameCustomizations(a: unknown, b: unknown): boolean {
  // Keeping your JSON stringify approach but protected against undefined
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export function VendorProvider({
  children,
  initialVendor = null,
}: VendorProviderProps) {
  // State
  const [selectedVendor, setSelectedVendorState] = useState<Vendor | null>(
    initialVendor
  );
  const [cart, setCart] = useState<CartItem[]>([]);
  const [currentVendorId, setCurrentVendorId] = useState<number | null>(
    initialVendor?.id ?? null
  );
  const [currentVendorName, setCurrentVendorName] = useState<string | null>(
    initialVendor?.name ?? null
  );
  const [isCartLoading, setIsCartLoading] = useState(true);
  const [lastCartUpdate, setLastCartUpdate] = useState<number>(Date.now());
  const [cartVersion] = useState<string>(CART_VERSION);

  // Calculate cart summary
  const cartSummary = useMemo<CartSummary>(() => {
    const subtotal = cart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    const tax = subtotal * TAX_RATE;
    const serviceFee = subtotal * SERVICE_FEE_RATE;
    const deliveryFee = cart.length > 0 ? DELIVERY_FEE : 0;
    const total = subtotal + tax + serviceFee + deliveryFee;
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const uniqueItems = cart.length;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(tax.toFixed(2)),
      serviceFee: Number(serviceFee.toFixed(2)),
      deliveryFee: Number(deliveryFee.toFixed(2)),
      total: Number(total.toFixed(2)),
      itemCount,
      uniqueItems,
    };
  }, [cart]);

  const currentVendorCart = useMemo<CurrentVendorCartSnapshot>(() => {
    return {
      vendor_id: currentVendorId,
      vendor_name: currentVendorName,
      item_count: cartSummary.itemCount,
      unique_items: cartSummary.uniqueItems,
      items: cart,
    };
  }, [cart, cartSummary.itemCount, cartSummary.uniqueItems, currentVendorId, currentVendorName]);

  /* ---------------------------------------------------------------------------
   * CART PERSISTENCE
   * ------------------------------------------------------------------------- */

  const saveCart = useCallback(() => {
    try {
      if (typeof window === "undefined") return;

      const cartData = {
        cart,
        vendorId: currentVendorId,
        vendorName: currentVendorName,
        lastUpdated: Date.now(),
        expiresAt: Date.now() + CART_EXPIRY_MS,
        version: CART_VERSION,
      };

      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));

      if (process.env.NODE_ENV === "development") {
        console.log("💾 Cart saved to localStorage:", {
          itemCount: cart.length,
          vendorId: currentVendorId,
          vendorName: currentVendorName,
        });
      }
    } catch (error) {
      console.error("Failed to save cart to localStorage:", error);
    }
  }, [cart, currentVendorId, currentVendorName]);

  const loadCart = useCallback(() => {
    try {
      if (typeof window === "undefined") return;

      setIsCartLoading(true);

      const storedData = localStorage.getItem(CART_STORAGE_KEY);
      if (!storedData) return;

      const cartData = safeJsonParse<any>(storedData);
      if (!cartData || !Array.isArray(cartData.cart)) {
        localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }

      // Check expiration
      if (cartData.expiresAt && Date.now() > Number(cartData.expiresAt)) {
        localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }

      // Check version compatibility
      if (cartData.version !== CART_VERSION) {
        localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }

      setCart(cartData.cart);
      setCurrentVendorId(cartData.vendorId ?? null);
      setCurrentVendorName(cartData.vendorName ?? null);
      setLastCartUpdate(cartData.lastUpdated ?? Date.now());

      if (process.env.NODE_ENV === "development") {
        console.log("📥 Cart loaded from localStorage:", {
          itemCount: cartData.cart.length,
          vendorId: cartData.vendorId,
          vendorName: cartData.vendorName,
        });
      }
    } catch (error) {
      console.error("Failed to load cart from localStorage:", error);
      try {
        localStorage.removeItem(CART_STORAGE_KEY);
      } catch {
        // ignore
      }
    } finally {
      setIsCartLoading(false);
    }
  }, []);

  // Load cart on mount
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Auto-save cart on changes (also clears storage when cart becomes empty)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isCartLoading) return;

    if (cart.length > 0) {
      saveCart();
    } else {
      // cart emptied via remove/update: ensure storage isn't stale
      try {
        localStorage.removeItem(CART_STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  }, [cart, isCartLoading, saveCart]);

  /* ---------------------------------------------------------------------------
   * CART OPERATIONS
   * ------------------------------------------------------------------------- */

  const clearCart = useCallback(() => {
    setCart([]);
    setCurrentVendorId(null);
    setCurrentVendorName(null);
    setLastCartUpdate(Date.now());

    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(CART_STORAGE_KEY);
      } catch {
        // ignore
      }
    }

    if (process.env.NODE_ENV === "development") {
      console.log("🗑️ Cart cleared");
    }
  }, []);

  const addToCart = useCallback(
    (
      item: MenuItem,
      quantity: number,
      customizations?: any,
      instructions?: string
    ) => {
      if (!selectedVendor) {
        throw new Error("Please select a restaurant first");
      }

      if (currentVendorId && currentVendorId !== selectedVendor.id) {
        throw new Error(
          "Cannot add items from different restaurants. Please clear your cart first."
        );
      }

      if (!item.is_available) {
        throw new Error("This item is currently unavailable");
      }

      if (!Number.isFinite(quantity) || quantity < 1) {
        throw new Error("Quantity must be at least 1");
      }

      setCart((prevCart) => {
        const existingIndex = prevCart.findIndex(
          (cartItem) =>
            cartItem.id === item.id &&
            sameCustomizations(cartItem.customizations, customizations)
        );

        if (existingIndex >= 0) {
          const updatedCart = [...prevCart];
          const existingItem = updatedCart[existingIndex];
          const newQty = existingItem.quantity + quantity;

          updatedCart[existingIndex] = {
            ...existingItem,
            quantity: newQty,
            special_instructions:
              instructions ?? existingItem.special_instructions,
            addedAt: Date.now(),
            totalPrice: existingItem.price * newQty,
          };

          return updatedCart;
        }

        const newItem: CartItem = {
          ...item,
          quantity,
          customizations,
          special_instructions: instructions,
          addedAt: Date.now(),
          totalPrice: item.price * quantity,
        };

        if (!currentVendorId) {
          setCurrentVendorId(selectedVendor.id);
          setCurrentVendorName(selectedVendor.name);
        }

        return [...prevCart, newItem];
      });

      setLastCartUpdate(Date.now());
    },
    [selectedVendor, currentVendorId]
  );

  const removeFromCart = useCallback((itemId: number, customizations?: any) => {
    setCart((prevCart) => {
      const filteredCart = prevCart.filter(
        (item) =>
          !(
            item.id === itemId &&
            sameCustomizations(item.customizations, customizations)
          )
      );

      if (filteredCart.length === 0) {
        setCurrentVendorId(null);
        setCurrentVendorName(null);
      }

      return filteredCart;
    });

    setLastCartUpdate(Date.now());
  }, []);

  const updateCartItem = useCallback(
    (
      itemId: number,
      updates: {
        quantity?: number;
        customizations?: any;
        instructions?: string;
      }
    ) => {
      const { quantity, customizations, instructions } = updates;

      setCart((prevCart) => {
        const itemIndex = prevCart.findIndex(
          (item) =>
            item.id === itemId &&
            sameCustomizations(item.customizations, customizations)
        );

        if (itemIndex === -1) return prevCart;

        const updatedCart = [...prevCart];
        const item = updatedCart[itemIndex];

        if (quantity !== undefined) {
          if (!Number.isFinite(quantity) || quantity <= 0) {
            updatedCart.splice(itemIndex, 1);
          } else {
            updatedCart[itemIndex] = {
              ...item,
              quantity,
              special_instructions:
                instructions !== undefined
                  ? instructions
                  : item.special_instructions,
              totalPrice: item.price * quantity,
              addedAt: Date.now(),
            };
          }
        } else if (instructions !== undefined) {
          updatedCart[itemIndex] = {
            ...item,
            special_instructions: instructions,
          };
        }

        if (updatedCart.length === 0) {
          setCurrentVendorId(null);
          setCurrentVendorName(null);
        }

        return updatedCart;
      });

      setLastCartUpdate(Date.now());
    },
    []
  );

  /* ---------------------------------------------------------------------------
   * VENDOR SELECTION
   * ------------------------------------------------------------------------- */

  const setSelectedVendor = useCallback(
    (vendor: Vendor | null) => {
      if (
        vendor &&
        currentVendorId &&
        currentVendorId !== vendor.id &&
        cart.length > 0
      ) {
        const userConfirmed = window.confirm(
          `You have items from "${currentVendorName}" in your cart. ` +
            `Switching to "${vendor.name}" will clear your current cart. Continue?`
        );

        if (!userConfirmed) return;

        clearCart();
      }

      setSelectedVendorState(vendor);

      if (vendor) {
        setCurrentVendorId(vendor.id);
        setCurrentVendorName(vendor.name);
      } else {
        setCurrentVendorId(null);
        setCurrentVendorName(null);
      }

      setLastCartUpdate(Date.now());
    },
    [cart.length, clearCart, currentVendorId, currentVendorName]
  );

  const clearSelectedVendor = useCallback(() => {
    setSelectedVendorState(null);
    setCurrentVendorId(null);
    setCurrentVendorName(null);
    setLastCartUpdate(Date.now());
  }, []);

  /* ---------------------------------------------------------------------------
   * VALIDATION & CHECKS
   * ------------------------------------------------------------------------- */

  const validateCartForVendor = useCallback(
    (vendorId: number): boolean => {
      if (cart.length === 0) return true;
      return cart.every((item) => item.vendor_id === vendorId);
    },
    [cart]
  );

  const hasCartConflict = useCallback(
    (vendorId: number): boolean => {
      return currentVendorId !== null && currentVendorId !== vendorId && cart.length > 0;
    },
    [currentVendorId, cart.length]
  );

  const isCartEmpty = cart.length === 0;

  /* ---------------------------------------------------------------------------
   * CONTEXT VALUE
   * ------------------------------------------------------------------------- */

  const contextValue: VendorContextType = useMemo(
    () => ({
      selectedVendor,
      setSelectedVendor,
      clearSelectedVendor,

      cart,
      cartSummary,
      currentVendorId,
      currentVendorName,

      currentVendorCart,

      addToCart,
      removeFromCart,
      updateCartItem,
      clearCart,

      validateCartForVendor,
      hasCartConflict,
      isCartEmpty,

      saveCart,
      loadCart,

      isCartLoading,
      lastCartUpdate,
      cartVersion,
    }),
    [
      selectedVendor,
      setSelectedVendor,
      clearSelectedVendor,
      cart,
      cartSummary,
      currentVendorId,
      currentVendorName,
      currentVendorCart,
      addToCart,
      removeFromCart,
      updateCartItem,
      clearCart,
      validateCartForVendor,
      hasCartConflict,
      isCartEmpty,
      saveCart,
      loadCart,
      isCartLoading,
      lastCartUpdate,
      cartVersion,
    ]
  );

  return (
    <VendorContext.Provider value={contextValue}>
      {children}
    </VendorContext.Provider>
  );
}

/* =============================================================================
 * HOOK
 * ============================================================================= */

export function useVendor(): VendorContextType {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error("useVendor must be used within a VendorProvider");
  }
  return context;
}
